import * as THREE from "three";

import type { EnemyRoleId } from "../content/schemas";
import { ELITE_AFFIXES } from "../content/elite-affixes";
import type { EnemyState } from "../simulation/snapshot";

const ROLE_COLORS: Record<EnemyRoleId, number> = {
  scout: 0x7a4a4a,
  skirmisher: 0x8b3a3a,
  turret: 0x6a5050,
  bomber: 0x9a4a2a,
  jammer: 0x5a6a8a,
  breacher: 0xa03030,
  carrier: 0x4a4a5a,
  sniper: 0x8a6a38,
  scavenger: 0x7d6a34,
  marauder: 0x9b5634,
  tender: 0x3f7180,
  sapper: 0x805d32,
  nullifier: 0x584c78,
  leech: 0x476c68,
  warden: 0x684032,
  harvester: 0x5f3828,
  sovereign: 0x71364f,
  leviathan: 0x493d3a,
  null_crown: 0x40345f,
  centurion: 0x4b4658,
  ace: 0xd4a017,
};

const ELITE_COLOR = 0xc9a227;

/**
 * Visual-only root scale. The industrial enemy-kit redesign authored thin
 * ~1–2.5 m parts that read as microscopic under the chase cam — that pass
 * (not the player-only SHIP_SCALE shrink) caused the "tiny enemies" report.
 * 1.55 restores peer combat readability next to the player (~3 m at SHIP_SCALE
 * 1.28). Sim hit tests stay on the fixed ~1.6 m enemy radius in simulation.ts.
 */
const ENEMY_VISUAL_SCALE = 1.55;

/**
 * Role-distinct low-poly industrial silhouettes for top-down threat ID.
 * Effective footprint after ENEMY_VISUAL_SCALE: ~2–5 m — readable peers to the
 * player ship, not oversized Phase-A cubes and not microscopic needles.
 */
export function createEnemyMesh(role: EnemyRoleId, metalTex: THREE.Texture): THREE.Group {
  const root = new THREE.Group();
  root.name = `enemy-${role}`;
  const baseMat = new THREE.MeshStandardMaterial({
    color: ROLE_COLORS[role],
    map: metalTex,
    metalness: 0.55,
    roughness: 0.48,
  });
  const darkMat = new THREE.MeshStandardMaterial({
    color: 0x2a2420,
    map: metalTex,
    metalness: 0.65,
    roughness: 0.55,
  });

  switch (role) {
    case "scout": {
      const body = new THREE.Mesh(new THREE.OctahedronGeometry(0.72, 0), baseMat);
      body.scale.set(0.55, 0.35, 1.35);
      root.add(body);
      const vane = new THREE.Mesh(new THREE.TetrahedronGeometry(0.55), darkMat);
      vane.scale.set(2.4, 0.18, 0.55);
      vane.position.set(0, 0.08, -0.15);
      vane.userData.keepTint = true;
      root.add(vane);
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.85, 5), darkMat);
      spike.rotation.x = Math.PI / 2;
      spike.position.set(0, 0, -1.25);
      spike.userData.keepTint = true;
      root.add(spike);
      const eye = new THREE.Mesh(
        new THREE.SphereGeometry(0.16, 8, 6),
        new THREE.MeshStandardMaterial({
          color: 0xff6644,
          emissive: 0x662200,
          emissiveIntensity: 0.7,
        }),
      );
      eye.position.set(0, 0.08, 0.85);
      eye.userData.accent = true;
      root.add(eye);
      break;
    }
    case "skirmisher": {
      const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.38, 1.7, 4, 8), baseMat);
      body.rotation.x = Math.PI / 2;
      root.add(body);
      const wing = new THREE.Mesh(new THREE.ConeGeometry(1.35, 0.18, 3), darkMat);
      wing.rotation.z = Math.PI / 2;
      wing.position.set(0, 0.04, -0.15);
      wing.userData.keepTint = true;
      root.add(wing);
      const canopy = new THREE.Mesh(new THREE.SphereGeometry(0.28, 8, 6), darkMat);
      canopy.scale.set(1, 0.55, 1.2);
      canopy.position.set(0, 0.32, 0.45);
      canopy.userData.keepTint = true;
      root.add(canopy);
      for (const side of [-1, 1]) {
        const engine = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.22, 0.6, 7), darkMat);
        engine.rotation.x = Math.PI / 2;
        engine.position.set(side * 0.52, -0.1, -1.05);
        engine.userData.keepTint = true;
        root.add(engine);
      }
      break;
    }
    case "turret": {
      const pad = new THREE.Mesh(new THREE.CylinderGeometry(1.45, 1.7, 0.42, 8), darkMat);
      pad.position.y = -0.35;
      pad.userData.keepTint = true;
      root.add(pad);
      const cupola = new THREE.Mesh(new THREE.SphereGeometry(0.78, 8, 6), baseMat);
      cupola.scale.set(1, 0.62, 1);
      cupola.position.y = 0.12;
      root.add(cupola);
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.16, 0.22, 2.2, 8),
        baseMat.clone(),
      );
      barrel.rotation.x = Math.PI / 2;
      barrel.position.set(0, 0.28, 1.1);
      root.add(barrel);
      const crate = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.36, 0.5, 6), darkMat);
      crate.position.set(-1.15, 0.05, -0.35);
      crate.userData.keepTint = true;
      root.add(crate);
      break;
    }
    case "bomber": {
      const body = new THREE.Mesh(new THREE.SphereGeometry(1.35, 8, 6), baseMat);
      body.scale.set(1.55, 0.42, 1.15);
      root.add(body);
      const bay = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.7, 1.2, 6), darkMat);
      bay.position.set(0, -0.42, 0.1);
      bay.userData.keepTint = true;
      root.add(bay);
      for (const side of [-1, 1]) {
        const engine = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.32, 0.8, 7), darkMat);
        engine.rotation.x = Math.PI / 2;
        engine.position.set(side * 1.2, 0, -1.05);
        engine.userData.keepTint = true;
        root.add(engine);
        const fin = new THREE.Mesh(new THREE.ConeGeometry(0.28, 0.75, 3), darkMat);
        fin.position.set(side * 1.25, 0.48, -0.8);
        fin.userData.keepTint = true;
        root.add(fin);
      }
      break;
    }
    case "jammer": {
      const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.78, 0), baseMat);
      root.add(core);
      const dish = new THREE.Mesh(
        new THREE.TorusGeometry(1.1, 0.15, 8, 18),
        new THREE.MeshStandardMaterial({
          color: 0x6ec6ff,
          emissive: 0x2a5080,
          emissiveIntensity: 0.9,
          metalness: 0.4,
          roughness: 0.35,
        }),
      );
      dish.rotation.x = Math.PI / 2;
      dish.position.y = 0.6;
      dish.userData.accent = true;
      root.add(dish);
      const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 1.0, 6), darkMat);
      mast.position.y = 0.75;
      mast.userData.keepTint = true;
      root.add(mast);
      break;
    }
    case "nullifier": {
      const core = new THREE.Mesh(new THREE.DodecahedronGeometry(0.8, 0), baseMat);
      root.add(core);
      const fieldMat = new THREE.MeshStandardMaterial({
        color: 0xb694ff,
        emissive: 0x3d246f,
        emissiveIntensity: 1.05,
        metalness: 0.35,
        roughness: 0.3,
      });
      for (const [radius, height] of [
        [1.18, 0.5],
        [1.58, 0.2],
      ] as const) {
        const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.11, 7, 22), fieldMat);
        ring.rotation.x = Math.PI / 2;
        ring.position.y = height;
        ring.userData.accent = true;
        root.add(ring);
      }
      for (const side of [-1, 1]) {
        const emitter = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.32, 1.45), darkMat);
        emitter.position.set(side * 1.1, 0, 0);
        emitter.userData.keepTint = true;
        root.add(emitter);
      }
      break;
    }
    case "leech": {
      const hull = new THREE.Mesh(new THREE.CapsuleGeometry(0.62, 1.7, 4, 8), baseMat);
      hull.rotation.x = Math.PI / 2;
      root.add(hull);
      const siphonMat = new THREE.MeshStandardMaterial({
        color: 0x6ff0cf,
        emissive: 0x174f48,
        emissiveIntensity: 1.05,
        metalness: 0.38,
        roughness: 0.32,
      });
      for (const side of [-1, 1]) {
        const capacitor = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 1.35, 9), siphonMat);
        capacitor.rotation.x = Math.PI / 2;
        capacitor.position.set(side * 0.92, 0.12, 0);
        capacitor.userData.accent = true;
        root.add(capacitor);
        const prong = new THREE.Mesh(new THREE.ConeGeometry(0.16, 1.1, 6), darkMat);
        prong.rotation.x = Math.PI / 2;
        prong.position.set(side * 0.75, 0.02, 1.3);
        prong.userData.keepTint = true;
        root.add(prong);
      }
      const returnCoil = new THREE.Mesh(new THREE.TorusGeometry(0.82, 0.12, 7, 20), siphonMat);
      returnCoil.rotation.x = Math.PI / 2;
      returnCoil.position.y = 0.52;
      returnCoil.userData.accent = true;
      root.add(returnCoil);
      break;
    }
    case "breacher": {
      const wedge = new THREE.Mesh(new THREE.ConeGeometry(1.15, 2.6, 5), baseMat);
      wedge.rotation.x = Math.PI / 2;
      root.add(wedge);
      const plow = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.15, 0.38, 6), darkMat);
      plow.rotation.x = Math.PI / 2;
      plow.position.set(0, -0.12, 0.95);
      plow.userData.keepTint = true;
      root.add(plow);
      for (const side of [-1, 1]) {
        const spike = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.75, 5), darkMat);
        spike.rotation.x = Math.PI / 2;
        spike.position.set(side * 0.7, 0.05, 1.2);
        spike.userData.keepTint = true;
        root.add(spike);
      }
      const engine = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.38, 0.75, 7), darkMat);
      engine.rotation.x = Math.PI / 2;
      engine.position.set(0, 0.05, -1.2);
      engine.userData.keepTint = true;
      root.add(engine);
      break;
    }
    case "carrier": {
      const hull = new THREE.Mesh(new THREE.CapsuleGeometry(0.7, 2.6, 4, 8), baseMat);
      hull.rotation.x = Math.PI / 2;
      root.add(hull);
      for (const side of [-1, 1]) {
        const pod = new THREE.Mesh(new THREE.CapsuleGeometry(0.32, 1.1, 3, 6), darkMat);
        pod.rotation.x = Math.PI / 2;
        pod.position.set(side * 1.35, -0.08, 0.15);
        pod.userData.keepTint = true;
        root.add(pod);
        const engine = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.28, 0.7, 7), darkMat);
        engine.rotation.x = Math.PI / 2;
        engine.position.set(side * 0.5, -0.15, -1.85);
        engine.userData.keepTint = true;
        root.add(engine);
      }
      const dish = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.07, 6, 12), darkMat);
      dish.rotation.x = Math.PI / 2;
      dish.position.set(0, 0.82, -0.35);
      dish.userData.keepTint = true;
      root.add(dish);
      break;
    }
    case "sniper": {
      const chassis = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.48, 1.7), baseMat);
      chassis.position.z = -0.2;
      root.add(chassis);
      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.16, 3.1, 7), darkMat);
      barrel.rotation.x = Math.PI / 2;
      barrel.position.set(0, 0.18, 1.25);
      barrel.userData.keepTint = true;
      root.add(barrel);
      for (const side of [-1, 1]) {
        const brace = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.22, 1.25), darkMat);
        brace.position.set(side * 0.82, -0.08, -0.25);
        brace.rotation.y = side * 0.18;
        brace.userData.keepTint = true;
        root.add(brace);
      }
      const scope = new THREE.Mesh(
        new THREE.SphereGeometry(0.2, 8, 6),
        new THREE.MeshStandardMaterial({
          color: 0xffb050,
          emissive: 0x663000,
          emissiveIntensity: 0.85,
        }),
      );
      scope.position.set(0, 0.5, 0.35);
      scope.userData.accent = true;
      root.add(scope);
      break;
    }
    case "scavenger": {
      const chassis = new THREE.Mesh(new THREE.BoxGeometry(1.55, 0.55, 2.1), baseMat);
      chassis.position.z = -0.15;
      root.add(chassis);
      for (const side of [-1, 1]) {
        const claw = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.2, 2.3), darkMat);
        claw.position.set(side * 1.15, -0.12, 0.72);
        claw.rotation.y = side * -0.14;
        claw.userData.keepTint = true;
        root.add(claw);
        const tooth = new THREE.Mesh(new THREE.ConeGeometry(0.2, 0.65, 5), darkMat);
        tooth.rotation.x = Math.PI / 2;
        tooth.position.set(side * 1.28, -0.1, 1.95);
        tooth.userData.keepTint = true;
        root.add(tooth);
        const bin = new THREE.Mesh(
          new THREE.BoxGeometry(0.62, 0.52, 0.85),
          new THREE.MeshStandardMaterial({
            color: 0xd4a43a,
            emissive: 0x5a3108,
            emissiveIntensity: 0.55,
            metalness: 0.45,
            roughness: 0.55,
          }),
        );
        bin.position.set(side * 0.78, 0.4, -0.65);
        bin.userData.accent = true;
        root.add(bin);
      }
      const intake = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.12, 7, 14), darkMat);
      intake.rotation.x = Math.PI / 2;
      intake.position.set(0, 0.12, 1.2);
      intake.userData.keepTint = true;
      root.add(intake);
      break;
    }
    case "marauder": {
      const hull = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.58, 2.55), baseMat);
      hull.position.z = -0.1;
      root.add(hull);
      const vault = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.8, 1.15), darkMat);
      vault.position.set(0, 0.48, -0.5);
      vault.userData.keepTint = true;
      root.add(vault);
      for (const side of [-1, 1]) {
        const clamp = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.24, 2.25), darkMat);
        clamp.position.set(side * 1.25, -0.06, 0.6);
        clamp.rotation.y = side * -0.12;
        clamp.userData.keepTint = true;
        root.add(clamp);
        const hook = new THREE.Mesh(new THREE.TorusGeometry(0.38, 0.13, 6, 10, Math.PI), darkMat);
        hook.rotation.x = Math.PI / 2;
        hook.rotation.z = (side * -Math.PI) / 2;
        hook.position.set(side * 1.35, -0.06, 1.75);
        hook.userData.keepTint = true;
        root.add(hook);
        const engine = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.3, 0.72, 7), darkMat);
        engine.rotation.x = Math.PI / 2;
        engine.position.set(side * 0.67, 0, -1.55);
        engine.userData.keepTint = true;
        root.add(engine);
      }
      const raidLight = new THREE.Mesh(
        new THREE.TorusGeometry(0.58, 0.1, 7, 16),
        new THREE.MeshStandardMaterial({
          color: 0xff6a32,
          emissive: 0x79230c,
          emissiveIntensity: 1,
          metalness: 0.4,
          roughness: 0.35,
        }),
      );
      raidLight.rotation.x = Math.PI / 2;
      raidLight.position.set(0, 0.38, 0.72);
      raidLight.userData.accent = true;
      root.add(raidLight);
      break;
    }
    case "tender": {
      const core = new THREE.Mesh(new THREE.CylinderGeometry(0.78, 1.0, 0.55, 8), baseMat);
      core.position.y = 0.05;
      root.add(core);
      const spine = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.38, 2.2), darkMat);
      spine.userData.keepTint = true;
      root.add(spine);
      for (const side of [-1, 1]) {
        const pod = new THREE.Mesh(new THREE.CapsuleGeometry(0.24, 0.95, 3, 7), darkMat);
        pod.rotation.x = Math.PI / 2;
        pod.position.set(side * 1.0, -0.05, -0.15);
        pod.userData.keepTint = true;
        root.add(pod);
      }
      for (const radius of [1.15, 1.55]) {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(radius, 0.09, 7, 22),
          new THREE.MeshStandardMaterial({
            color: 0x76d7e8,
            emissive: 0x164d62,
            emissiveIntensity: 0.95,
            metalness: 0.35,
            roughness: 0.3,
          }),
        );
        ring.rotation.x = Math.PI / 2;
        ring.position.y = radius === 1.15 ? 0.42 : 0.2;
        ring.userData.accent = true;
        root.add(ring);
      }
      break;
    }
    case "sapper": {
      const hull = new THREE.Mesh(new THREE.CapsuleGeometry(0.56, 1.7, 4, 8), baseMat);
      hull.rotation.x = Math.PI / 2;
      root.add(hull);
      const siphon = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.72, 0.58, 8), darkMat);
      siphon.position.y = 0.46;
      siphon.userData.keepTint = true;
      root.add(siphon);
      for (const side of [-1, 1]) {
        const prong = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.25, 1.85), darkMat);
        prong.position.set(side * 0.88, 0, 0.35);
        prong.rotation.y = side * -0.16;
        prong.userData.keepTint = true;
        root.add(prong);
      }
      const drainCoil = new THREE.Mesh(
        new THREE.TorusGeometry(0.72, 0.13, 7, 18),
        new THREE.MeshStandardMaterial({
          color: 0xffbc52,
          emissive: 0x74320d,
          emissiveIntensity: 1.05,
          metalness: 0.38,
          roughness: 0.32,
        }),
      );
      drainCoil.rotation.x = Math.PI / 2;
      drainCoil.position.set(0, 0.42, 0.55);
      drainCoil.userData.accent = true;
      root.add(drainCoil);
      break;
    }
    case "warden": {
      const citadel = new THREE.Mesh(new THREE.CylinderGeometry(1.55, 1.8, 0.72, 8), baseMat);
      citadel.position.y = -0.1;
      root.add(citadel);
      const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.78, 1.15, 0.72, 8), darkMat);
      crown.position.y = 0.55;
      crown.userData.keepTint = true;
      root.add(crown);
      for (const side of [-1, 1]) {
        const battery = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.42, 1.7), darkMat);
        battery.position.set(side * 1.38, 0.18, 0.15);
        battery.userData.keepTint = true;
        root.add(battery);
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.12, 0.16, 1.7, 7),
          baseMat.clone(),
        );
        barrel.rotation.x = Math.PI / 2;
        barrel.position.set(side * 1.38, 0.35, 1.15);
        root.add(barrel);
      }
      const shieldRing = new THREE.Mesh(
        new THREE.TorusGeometry(2.05, 0.12, 8, 24),
        new THREE.MeshStandardMaterial({
          color: 0xff9a52,
          emissive: 0x743014,
          emissiveIntensity: 0.9,
        }),
      );
      shieldRing.rotation.x = Math.PI / 2;
      shieldRing.position.y = 0.35;
      shieldRing.userData.accent = true;
      root.add(shieldRing);
      break;
    }
    case "harvester": {
      const hull = new THREE.Mesh(new THREE.BoxGeometry(2.8, 0.8, 3.4), baseMat);
      hull.position.z = -0.25;
      root.add(hull);

      const crusher = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.8, 2.7, 10), darkMat);
      crusher.rotation.z = Math.PI / 2;
      crusher.position.set(0, 0.35, 1.45);
      crusher.userData.keepTint = true;
      root.add(crusher);

      const intakeGlow = new THREE.Mesh(
        new THREE.TorusGeometry(0.92, 0.16, 8, 22),
        new THREE.MeshStandardMaterial({
          color: 0xf0a548,
          emissive: 0x7b390d,
          emissiveIntensity: 1.1,
          metalness: 0.5,
          roughness: 0.32,
        }),
      );
      intakeGlow.rotation.x = Math.PI / 2;
      intakeGlow.position.set(0, 0.4, 1.85);
      intakeGlow.userData.accent = true;
      root.add(intakeGlow);

      for (const side of [-1, 1]) {
        const arm = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.32, 3.3), darkMat);
        arm.position.set(side * 1.85, -0.05, 0.45);
        arm.rotation.y = side * -0.16;
        arm.userData.keepTint = true;
        root.add(arm);

        const claw = new THREE.Mesh(new THREE.ConeGeometry(0.34, 1.15, 5), darkMat);
        claw.rotation.x = Math.PI / 2;
        claw.position.set(side * 2.08, -0.03, 2.2);
        claw.userData.keepTint = true;
        root.add(claw);

        const cargo = new THREE.Mesh(
          new THREE.BoxGeometry(0.9, 0.78, 1.25),
          new THREE.MeshStandardMaterial({
            color: 0xb7832f,
            emissive: 0x4e2608,
            emissiveIntensity: 0.65,
            metalness: 0.5,
            roughness: 0.5,
          }),
        );
        cargo.position.set(side * 1.45, 0.62, -1.15);
        cargo.userData.accent = true;
        root.add(cargo);
      }
      break;
    }
    case "sovereign": {
      const citadel = new THREE.Mesh(new THREE.CylinderGeometry(1.7, 2.15, 0.85, 10), baseMat);
      citadel.position.y = 0.05;
      root.add(citadel);
      const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.65, 1.2, 0.9, 8), darkMat);
      crown.position.y = 0.72;
      crown.userData.keepTint = true;
      root.add(crown);
      for (const side of [-1, 1]) {
        const battery = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.48, 2.4), darkMat);
        battery.position.set(side * 1.65, 0.12, 0.2);
        battery.userData.keepTint = true;
        root.add(battery);
      }
      for (const radius of [2.25, 2.65]) {
        const ring = new THREE.Mesh(
          new THREE.TorusGeometry(radius, 0.12, 8, 28),
          new THREE.MeshStandardMaterial({
            color: 0xe27bc2,
            emissive: 0x651a50,
            emissiveIntensity: 1.05,
            metalness: 0.42,
            roughness: 0.3,
          }),
        );
        ring.rotation.x = Math.PI / 2;
        ring.position.y = radius === 2.25 ? 0.45 : 0.18;
        ring.userData.accent = true;
        root.add(ring);
      }
      break;
    }
    case "leviathan": {
      const foundry = new THREE.Mesh(new THREE.BoxGeometry(3.8, 1.05, 4.4), baseMat);
      foundry.position.y = 0.05;
      root.add(foundry);

      const furnace = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.35, 1.45, 10), darkMat);
      furnace.position.y = 0.95;
      furnace.userData.keepTint = true;
      root.add(furnace);

      const hotMat = new THREE.MeshStandardMaterial({
        color: 0xffb04a,
        emissive: 0x8b2d08,
        emissiveIntensity: 1.25,
        metalness: 0.38,
        roughness: 0.3,
      });
      for (const side of [-1, 1]) {
        const dock = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.6, 3.7), darkMat);
        dock.position.set(side * 2.35, 0, 0.15);
        dock.userData.keepTint = true;
        root.add(dock);

        const gantry = new THREE.Mesh(new THREE.TorusGeometry(0.72, 0.14, 7, 18), hotMat);
        gantry.rotation.x = Math.PI / 2;
        gantry.position.set(side * 2.35, 0.38, 1.72);
        gantry.userData.accent = true;
        root.add(gantry);
      }
      for (const offset of [-0.58, 0, 0.58]) {
        const vent = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.22, 1.5), hotMat);
        vent.position.set(offset, 1.18, 0);
        vent.userData.accent = true;
        root.add(vent);
      }
      break;
    }
    case "null_crown": {
      const core = new THREE.Mesh(new THREE.CylinderGeometry(1.45, 1.95, 1.05, 12), baseMat);
      core.position.y = 0.1;
      root.add(core);

      const nullMat = new THREE.MeshStandardMaterial({
        color: 0xb69cff,
        emissive: 0x352066,
        emissiveIntensity: 1.2,
        metalness: 0.45,
        roughness: 0.28,
      });
      for (const radius of [2.15, 2.75, 3.3]) {
        const lattice = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.11, 8, 32), nullMat);
        lattice.rotation.x = Math.PI / 2;
        lattice.position.y = 0.45 - radius * 0.08;
        lattice.userData.accent = true;
        root.add(lattice);
      }
      for (let i = 0; i < 6; i += 1) {
        const angle = (i / 6) * Math.PI * 2;
        const spire = new THREE.Mesh(new THREE.ConeGeometry(0.24, 1.7, 6), darkMat);
        spire.position.set(Math.cos(angle) * 1.55, 0.78, Math.sin(angle) * 1.55);
        spire.userData.keepTint = true;
        root.add(spire);
      }
      const eye = new THREE.Mesh(new THREE.OctahedronGeometry(0.55, 0), nullMat);
      eye.position.y = 1.35;
      eye.userData.accent = true;
      root.add(eye);
      break;
    }
    case "centurion": {
      const bastion = new THREE.Mesh(new THREE.CylinderGeometry(2.15, 2.6, 1.15, 12), baseMat);
      bastion.position.y = 0.05;
      root.add(bastion);
      const counterMat = new THREE.MeshStandardMaterial({
        color: 0xffd27a,
        emissive: 0x7a3d12,
        emissiveIntensity: 1.25,
        metalness: 0.44,
        roughness: 0.3,
      });
      for (let i = 0; i < 8; i += 1) {
        const angle = (i / 8) * Math.PI * 2;
        const battery = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.48, 1.45), darkMat);
        battery.position.set(Math.cos(angle) * 2.4, 0.24, Math.sin(angle) * 2.4);
        battery.rotation.y = -angle;
        battery.userData.keepTint = true;
        root.add(battery);
      }
      for (const radius of [2.85, 3.35]) {
        const pulseRing = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.13, 8, 36), counterMat);
        pulseRing.rotation.x = Math.PI / 2;
        pulseRing.position.y = radius === 2.85 ? 0.48 : 0.2;
        pulseRing.userData.accent = true;
        root.add(pulseRing);
      }
      const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.7, 0), counterMat);
      core.position.y = 1.35;
      core.userData.accent = true;
      root.add(core);
      break;
    }
    case "ace": {
      const body = new THREE.Mesh(new THREE.ConeGeometry(0.72, 2.9, 6), baseMat);
      body.rotation.x = Math.PI / 2;
      root.add(body);
      const wing = new THREE.Mesh(new THREE.TetrahedronGeometry(0.7), darkMat);
      wing.scale.set(2.6, 0.12, 0.7);
      wing.position.set(0, 0.04, -0.3);
      wing.userData.keepTint = true;
      root.add(wing);
      const canopy = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), darkMat);
      canopy.scale.set(1, 0.55, 1.3);
      canopy.position.set(0, 0.32, 0.5);
      canopy.userData.keepTint = true;
      root.add(canopy);
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(1.35, 0.11, 8, 20),
        new THREE.MeshStandardMaterial({
          color: 0xffd700,
          emissive: 0x664400,
          emissiveIntensity: 0.85,
        }),
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 0.28;
      ring.userData.accent = true;
      root.add(ring);
      break;
    }
  }

  root.scale.setScalar(ENEMY_VISUAL_SCALE);
  root.userData.role = role;
  return root;
}

export function applyEnemyVisual(
  group: THREE.Group,
  enemy: EnemyState,
  prevHull: number,
  flashTimer: number,
  dt: number,
  bounty = false,
): number {
  group.position.set(enemy.x, enemy.altitude, enemy.y);
  group.rotation.y = -enemy.facing;

  let nextFlash = Math.max(0, flashTimer - dt);
  if (enemy.hull < prevHull) nextFlash = 0.22;

  const tint = enemy.isAce ? 0xffd700 : enemy.elite ? ELITE_COLOR : ROLE_COLORS[enemy.role];
  const affixColor = enemy.eliteAffix ? ELITE_AFFIXES[enemy.eliteAffix].color : tint;
  const flash = nextFlash > 0 ? 1 - nextFlash / 0.22 : 0;

  group.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    if (child.userData.accent || child.userData.keepTint) return;
    const mat = child.material as THREE.MeshStandardMaterial;
    if (!mat.emissive) return;
    mat.color.setHex(enemy.eliteAffix ? affixColor : tint);
    if (flash > 0) {
      mat.emissive.setHex(0xffaa44);
      mat.emissiveIntensity = flash * 2.5;
    } else if (bounty) {
      mat.emissive.setHex(0xff6a22);
      mat.emissiveIntensity = 0.55;
    } else {
      mat.emissive.setHex(0x000000);
      mat.emissiveIntensity = 0;
    }
  });

  return nextFlash;
}

export function disposeEnemyMesh(group: THREE.Group): void {
  group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry.dispose();
      const mat = child.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
    }
  });
}
